import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import { useSupabaseStorage } from '../../../hooks/useSupabaseStorage';
import { supabase } from '../../../lib/supabase';

interface UploadedFile {
  url: string;
  path: string;
  fileName: string;
  type: 'image' | 'video';
  thumbnailUrl?: string;
  thumbnailPath?: string;
}

const FrenchettePage = () => {
  const { t, i18n } = useTranslation();
  const [expandedBox, setExpandedBox] = useState<number | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: number]: UploadedFile }>({});
  const [uploadingBoxes, setUploadingBoxes] = useState<{ [key: number]: boolean }>({});
  const [uploadingThumbnails, setUploadingThumbnails] = useState<{ [key: number]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const thumbnailInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const { uploadFile, deleteFile, uploading, uploadProgress = 0 } = useSupabaseStorage();

  const projectLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Wild Cherry', href: '/project/wildcherry' },
    { name: "Le Veau d'Or", href: '/project/leveaudor' },
    { name: 'Le Rock', href: '/project/lerock' },
    { name: 'Frenchette', href: '/project/frenchette' },
  ];

  const handleMobileNavClick = (href: string) => {
    setIsMenuOpen(false);
    // Scroll to top on mobile when navigating
    if (window.innerWidth < 768) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  // Load existing files from database on component mount
  useEffect(() => {
    loadExistingFiles();
  }, []);

  const loadExistingFiles = async () => {
    try {
      console.log('Loading existing files from database...');
      const { data, error } = await supabase
        .from('frenchette_uploads')
        .select('*')
        .order('box_index');

      if (error) {
        console.error('Error loading files:', error);
        setError(`Failed to load files: ${error.message}`);
        return;
      }

      console.log('Loaded files from database:', data);

      const filesMap: { [key: number]: UploadedFile } = {};
      data?.forEach((file) => {
        filesMap[file.box_index] = {
          url: file.file_url,
          path: file.file_path,
          fileName: file.file_name,
          type: file.file_type as 'image' | 'video',
          thumbnailUrl: file.thumbnail_url || undefined,
          thumbnailPath: file.thumbnail_path || undefined,
        };
      });

      setUploadedFiles(filesMap);
      console.log('Files loaded successfully:', filesMap);
    } catch (error) {
      console.error('Error loading files:', error);
      setError('Failed to load files from database');
    } finally {
      setLoading(false);
    }
  };

  const saveFileToDatabase = async (index: number, fileData: UploadedFile) => {
    try {
      console.log('Saving file to database:', { index, fileData });

      const { data, error } = await supabase
        .from('frenchette_uploads')
        .upsert({
          box_index: index,
          file_name: fileData.fileName,
          file_path: fileData.path,
          file_url: fileData.url,
          file_type: fileData.type,
          thumbnail_url: fileData.thumbnailUrl || null,
          thumbnail_path: fileData.thumbnailPath || null,
          uploaded_at: new Date().toISOString(),
        })
        .select();

      if (error) {
        console.error('Database save error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      console.log('File saved to database successfully:', data);
      return data;
    } catch (error) {
      console.error('Database save error:', error);
      throw error;
    }
  };

  const removeFileFromDatabase = async (index: number) => {
    try {
      console.log('Removing file from database, box index:', index);

      const { error } = await supabase
        .from('frenchette_uploads')
        .delete()
        .eq('box_index', index);

      if (error) {
        console.error('Database delete error:', error);
        throw new Error(`Database delete error: ${error.message}`);
      }

      console.log('File removed from database successfully');
    } catch (error) {
      console.error('Database delete error:', error);
      throw error;
    }
  };

  const handleBoxClick = (index: number) => {
    // Only allow expand on desktop (md and above)
    if (window.innerWidth >= 768) {
      setExpandedBox(index);
    }
  };

  const handleFileUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      console.log('Starting file upload for box:', index, 'File:', file.name);
      setUploadingBoxes((prev) => ({ ...prev, [index]: true }));
      setError(null);

      // Upload to Supabase Storage
      const result = await uploadFile(file);
      console.log('File uploaded to storage:', result);

      // Determine file type based on panel index
      const fileType: 'image' | 'video' = index === 8 ? 'video' : 'image';

      const fileData: UploadedFile = {
        url: result.url,
        path: result.path,
        fileName: result.fileName,
        type: fileType,
      };

      // Save to database
      await saveFileToDatabase(index, fileData);

      // Update local state
      setUploadedFiles((prev) => ({
        ...prev,
        [index]: fileData,
      }));

      setExpandedBox(null);
      console.log('Upload completed successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      setError(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingBoxes((prev) => ({ ...prev, [index]: false }));
      // Reset file input
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index]!.value = '';
      }
    }
  };

  const handleThumbnailUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const existingFile = uploadedFiles[index];
    if (!existingFile || existingFile.type !== 'video') return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file for thumbnail');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Thumbnail image must be smaller than 5MB');
      return;
    }

    try {
      console.log('Starting thumbnail upload for box:', index, 'File:', file.name);
      setUploadingThumbnails(prev => ({ ...prev, [index]: true }));
      setError(null);

      const result = await uploadFile(file);
      console.log('Thumbnail uploaded to storage:', result);

      const updatedFileData: UploadedFile = {
        ...existingFile,
        thumbnailUrl: result.url,
        thumbnailPath: result.path,
      };

      await saveFileToDatabase(index, updatedFileData);

      setUploadedFiles(prev => ({
        ...prev,
        [index]: updatedFileData,
      }));

      console.log('Thumbnail upload completed successfully');
    } catch (error) {
      console.error('Thumbnail upload failed:', error);
      setError(`Thumbnail upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingThumbnails(prev => ({ ...prev, [index]: false }));
      if (thumbnailInputRefs.current[index]) {
        thumbnailInputRefs.current[index]!.value = '';
      }
    }
  };

  const handleRemoveFile = async (index: number, event: React.MouseEvent) => {
    event.stopPropagation();

    const file = uploadedFiles[index];
    if (!file) return;

    try {
      console.log('Removing file:', file);
      setError(null);

      // Delete from Supabase Storage
      await deleteFile(file.path);
      console.log('File deleted from storage');

      // Delete thumbnail if exists
      if (file.thumbnailPath) {
        await deleteFile(file.thumbnailPath);
        console.log('Thumbnail deleted from storage');
      }

      // Remove from database
      await removeFileFromDatabase(index);
      console.log('File removed from database');

      // Update local state
      setUploadedFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[index];
        return newFiles;
      });

      setExpandedBox(null);
      console.log('File removal completed successfully');
    } catch (error) {
      console.error('Delete failed:', error);
      setError(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": "Frenchette - Creative Design Project",
    "description": "Frenchette project by Dava Nasr - Creative graphic design and brand development",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/project/frenchette`,
    "creator": {
      "@type": "Person",
      "name": "Dava Nasr",
      "jobTitle": "Graphic Designer"
    },
    "image": "https://static.readdy.ai/image/b035185ce40665b0d42a9231037815e9/9ff4033aeb88c066276a29640dfb7992.jpeg"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-600 mb-4">
              <i className="ri-loader-4-line text-6xl animate-spin"></i>
            </div>
            <p className="text-gray-600 text-xl">Loading your files...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <SEOHead
        title="Frenchette - Dava Nasr Portfolio"
        description="Frenchette creative design project by Dava Nasr. Graphic design and brand development work."
        keywords="Frenchette, graphic design, brand development, creative design, Dava Nasr"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/project/frenchette`}
        schemaData={schemaData}
      />
      
      <Header />

      {/* Hamburger Menu */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="bg-black border border-white/20 shadow-lg rounded-lg p-2 md:p-6 hover:bg-gray-900 transition-colors duration-200 cursor-pointer"
        >
          <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 md:w-10 md:h-10 flex items-center justify-center text-white`} style={{ strokeWidth: '3px', fontWeight: 'bold' }}></i>
        </button>

        {/* Mobile Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute top-full right-0 mt-1 bg-black border border-white/20 shadow-lg rounded-lg py-0.5 md:py-4 min-w-[100px] md:min-w-[200px]">
            {projectLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => handleMobileNavClick(link.href)}
                className="block px-3 py-1 md:px-6 md:py-3 text-xs md:text-xl font-medium text-white hover:bg-gray-900 hover:text-teal-400 transition-colors duration-200 cursor-pointer whitespace-nowrap text-right"
              >
                {link.name}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 mx-8 mt-4 rounded">
          <div className="flex items-center">
            <i className="ri-error-warning-line mr-2"></i>
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-300 hover:text-red-100">
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {/* Main Content - Positioned Higher */}
      <div className="flex-1 flex flex-col items-center pt-32 pb-16">
        <h1 className="text-4xl md:text-6xl lg:text-8xl xl:text-9xl font-bold text-white mb-4">frenchette</h1>
        
        {/* Caption Box */}
        <div className="max-w-2xl mx-auto text-center mb-16 px-6 md:px-0">
          <p className="text-sm md:text-2xl italic text-gray-300 leading-relaxed">
            {t('frenchetteCaption')}
          </p>
        </div>
      </div>

      {/* 3x3 Grid of Image Boxes */}
      <div className="w-full max-w-6xl mx-auto px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 9 }, (_, index) => (
            <div key={index} className="relative">
              <input
                ref={(el) => (fileInputRefs.current[index] = el)}
                type="file"
                accept={index === 8 ? 'video/*' : 'image/jpeg,image/jpg'}
                onChange={(e) => handleFileUpload(index, e)}
                className="hidden"
              />
              <input
                ref={(el) => (thumbnailInputRefs.current[index] = el)}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={(e) => handleThumbnailUpload(index, e)}
                className="hidden"
              />
              <div
                className={`relative bg-gray-800 border-2 border-gray-600 transition-all duration-300 cursor-pointer hover:border-gray-500 ${
                  uploadedFiles[index] && uploadedFiles[index].type === 'video' ? 'aspect-video md:aspect-square' : 'aspect-auto md:aspect-square'
                } h-auto md:h-60 lg:h-80`}
                onClick={() => {
                  if (uploadedFiles[index]) {
                    // Only allow expand on desktop (md and above)
                    if (window.innerWidth >= 768) {
                      setExpandedBox(index);
                    }
                  } else {
                    fileInputRefs.current[index]?.click();
                  }
                }}
              >
                {uploadingBoxes[index] ? (
                  <div className="text-center">
                    <div className="text-gray-300 mb-1 md:mb-4">
                      <i className="ri-upload-cloud-line text-lg animate-pulse md:text-6xl"></i>
                    </div>
                    <p className="text-gray-300 text-xs md:text-lg">
                      {index === 8 ? 'Uploading Video...' : 'Uploading Image...'}
                    </p>
                    <div className="w-8 bg-gray-600 rounded-full h-0.5 mt-1 md:w-32 md:h-2 md:mt-4">
                      <div
                        className="bg-blue-400 h-0.5 rounded-full transition-all duration-300 md:h-2"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                ) : uploadedFiles[index] ? (
                  <div className="relative w-full h-full">
                    {uploadedFiles[index].type === 'video' ? (
                      <>
                        {/* Mobile Video Display */}
                        <div className="block md:hidden">
                          {uploadedFiles[index].thumbnailUrl ? (
                            <div 
                              className="relative w-full h-full cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                const container = e.currentTarget;
                                const videoElement = document.createElement('video');
                                videoElement.src = uploadedFiles[index].url;
                                videoElement.controls = true;
                                videoElement.autoplay = true;
                                videoElement.playsInline = true;
                                videoElement.className = 'w-full h-full object-contain';
                                
                                // Clear the container and add video immediately (no overlay)
                                container.innerHTML = '';
                                container.appendChild(videoElement);
                              }}
                            >
                              <img 
                                src={uploadedFiles[index].thumbnailUrl} 
                                alt="Video thumbnail"
                                className="w-full h-full object-cover object-center"
                              />
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="bg-black bg-opacity-60 rounded-full p-4">
                                  <i className="ri-play-fill text-white text-4xl w-8 h-8 flex items-center justify-center"></i>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <video 
                              src={uploadedFiles[index].url} 
                              className="w-full h-full object-contain" 
                              controls 
                              playsInline
                            />
                          )}
                        </div>
                        
                        {/* Desktop Video Display */}
                        <div className="hidden md:block relative w-full h-full">
                          {uploadedFiles[index].thumbnailUrl ? (
                            <>
                              <img 
                                src={uploadedFiles[index].thumbnailUrl} 
                                alt="Video thumbnail"
                                className="w-full h-full object-cover object-[center_20%]"
                              />
                              {/* Video Play Button Overlay */}
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="bg-black bg-opacity-60 rounded-full p-4">
                                  <i className="ri-play-fill text-white text-4xl w-8 h-8 flex items-center justify-center"></i>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <video src={uploadedFiles[index].url} className="w-full h-full object-cover" muted />
                              {/* Video Play Button Overlay */}
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="bg-black bg-opacity-60 rounded-full p-4">
                                  <i className="ri-play-fill text-white text-4xl w-8 h-8 flex items-center justify-center"></i>
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Thumbnail Upload Button for Videos */}
                        {!uploadedFiles[index].thumbnailUrl && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              thumbnailInputRefs.current[index]?.click();
                            }}
                            className="absolute top-2 right-2 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 md:px-3 md:py-2 rounded text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap shadow-lg border border-blue-500 hidden md:block"
                            disabled={uploadingThumbnails[index]}
                          >
                            {uploadingThumbnails[index] ? (
                              <>
                                <i className="ri-loader-4-line animate-spin mr-1"></i>
                                <span className="hidden md:inline">Uploading...</span>
                                <span className="md:hidden">...</span>
                              </>
                            ) : (
                              <>
                                <i className="ri-image-add-line mr-1"></i>
                                <span className="hidden md:inline">Add Thumbnail</span>
                                <span className="md:hidden">Thumb</span>
                              </>
                            )}
                          </button>
                        )}

                        {/* Replace Thumbnail Button for Videos */}
                        {uploadedFiles[index].thumbnailUrl && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              thumbnailInputRefs.current[index]?.click();
                            }}
                            className="absolute top-2 right-2 bg-green-600 hover:bg-green-700 text-white px-2 py-1 md:px-3 md:py-2 rounded text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap shadow-lg border border-green-500 hidden"
                            disabled={uploadingThumbnails[index]}
                          >
                            {uploadingThumbnails[index] ? (
                              <>
                                <i className="ri-loader-4-line animate-spin mr-1"></i>
                                <span className="hidden md:inline">Updating...</span>
                                <span className="md:hidden">...</span>
                              </>
                            ) : (
                              <>
                                <i className="ri-image-edit-line mr-1"></i>
                                <span className="hidden md:inline">Change Thumbnail</span>
                                <span className="md:hidden">Edit</span>
                              </>
                            )}
                          </button>
                        )}
                      </>
                    ) : (
                      <img
                        src={uploadedFiles[index].url}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-auto md:w-full md:h-full md:object-cover md:object-top"
                      />
                    )}
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-gray-400 mb-1 md:mb-4">
                        <i className={`${index === 8 ? 'ri-video-line' : 'ri-image-line'} text-lg md:text-6xl`}></i>
                      </div>
                      <p className="text-gray-300 text-xs md:text-lg">
                        {index === 8 ? 'Upload Video' : `Upload Image ${index + 1}`}
                      </p>
                      <p className="text-gray-400 text-xs mt-0.5 hidden md:block">
                        {index === 8 ? 'Video Files Only' : 'JPG Only'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Spacing */}
      <div className="pb-8 md:pb-32"></div>

      {/* Expanded Overlay - Desktop Only */}
      {expandedBox !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 items-center justify-center hidden md:flex"
          onClick={() => setExpandedBox(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {uploadedFiles[expandedBox] ? (
              <>
                {uploadedFiles[expandedBox].type === 'video' ? (
                  <video
                    src={uploadedFiles[expandedBox].url}
                    className="max-w-[70vw] max-h-[80vh] object-contain"
                    controls
                    autoPlay
                  />
                ) : (
                  <img
                    src={uploadedFiles[expandedBox].url}
                    alt={`Upload ${expandedBox + 1}`}
                    className="max-w-[70vw] max-h-[80vh] object-contain"
                  />
                )}
                <button
                  onClick={() => setExpandedBox(null)}
                  className="absolute top-4 left-4 bg-black/50 text-white rounded-full p-2 hover:bg-black/70 transition-colors"
                >
                  <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
                </button>
              </>
            ) : (
              <div className="text-center p-8 bg-gray-900 rounded-lg max-w-md">
                <div className="text-white mb-4">
                  <i
                    className={`${expandedBox === 8 ? 'ri-video-line' : 'ri-image-line'} text-8xl w-24 h-24 flex items-center justify-center mx-auto`}
                  ></i>
                </div>
                <p className="text-white text-2xl mb-4">
                  {expandedBox === 8 ? 'Click to Upload Video' : 'Click to Upload Image'}
                </p>
                <p className="text-gray-300 text-lg mb-2">
                  {expandedBox === 8 ? 'Supported: MP4, MOV, AVI' : 'Supported: JPEG, JPG, PNG'}
                </p>
                <p className="text-gray-400 text-sm mb-6">
                  {expandedBox === 8 ? 'Files will be stored in Supabase Storage. You can add a custom thumbnail after upload.' : 'Recommended size: 16:9 aspect ratio for best display'}
                </p>
                <button
                  onClick={() => fileInputRefs.current[expandedBox]?.click()}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors mr-4 whitespace-nowrap cursor-pointer"
                >
                  Select File
                </button>
                <button
                  onClick={() => setExpandedBox(null)}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default FrenchettePage;
